import PropTypes from 'prop-types';
import React from 'react';
import StockRunLeadTimeLabel from '../../lead-times/StockRunLeadTimeLabel';
import { QuoteInventoryItemViewModel } from './QuoteInventoryItemViewModel';

const weightCategoriesList = (weightCategories) => (
  <ul className="list-unstyled mb-2 p-0">
    {weightCategories.map((wc) => (
      <li key={wc}>{wc}</li>
    ))}
  </ul>
);

const Availability = ({ inventoryItem }) => {
  if (inventoryItem.inStock) {
    if (inventoryItem.minimumLeadTime === 1) {
      return (
        <span className="inventory-item-lead-time">Next day</span>
      );
    }
    return `${inventoryItem.minimumLeadTime} business days`;
  }

  return (
    <StockRunLeadTimeLabel
      availableOn={inventoryItem.availableOn}
      minimumNumberOfDays={inventoryItem.minimumLeadTime}
    />
  );
};

const Row = ({
  product,
  productGroup,
  inventoryItem,
  onProductSelected,
  inventoryItemId,
  canSelectInventoryItem,
  highlightedInventoryItemId
}) => {
  const displayName = <strong>{inventoryItem.name}</strong>;

  const {
    name,
    notes,
    paintColor,
    restrictedReason,
    isConsignment,
    consignmentDealerBranchName,
    productOptions,
    condition,
    carrierName,
    lugging,
    thumb
  } = inventoryItem;

  const {
    dimensions,
    partNumber
  } = product;

  const {
    imageUrl
  } = productGroup;

  const displayCondition = condition.charAt(0).toUpperCase() + condition.slice(1);
  const isNewCondition = condition.toLowerCase() === 'new';

  const isItemSelected = inventoryItemId === inventoryItem.id;
  const isItemHighlighted = highlightedInventoryItemId && inventoryItem.id === highlightedInventoryItemId && !isItemSelected && !inventoryItem.isAddedToQuote;

  return (
    <tr className={isItemHighlighted ? 'inventory-item-highlighted' : ''}>
      <td>
        <img
          src={imageUrl}
          className="img-fluid"
          alt={name}
          style={{ maxWidth: 100 }}
        />
      </td>

      <td className="inventory-item-description">
        {displayName}

        <div className="mb-2">{partNumber}</div>

        <div className="dimensions">
          {dimensions.map((dimension, idx) => (
            <div className="row" key={idx}>
              <div className="col-auto text-nowrap">{`${dimension.name}:`}</div>
              <div className="col-auto text-break-line">{dimension.value}</div>
            </div>
          ))}
                    
          {paintColor && (
            <div className="row">
              <div className="col-auto text-nowrap">Paint:</div>
              <div className="col-auto text-break-line">{paintColor}</div>
            </div>
          )}
        </div>

        {productOptions.length > 0 && (
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              marginTop: 5,
              marginBottom: 0,
            }}
          >
            {productOptions.map((x, idx) => (
              <li key={idx}>{x.name}</li>
            ))}
          </ul>
        )}
        {thumb && (
          <div
            className="dimensions"
            style={{
              listStyle: 'none',
              padding: 0,
              marginTop: 5,
              marginBottom: 0,
            }}
          >
            <div className="row">
              <div className="col-auto text-nowrap">Tip Radius:</div>
              <div className="col-auto text-break-line">{thumb.tipRadius}</div>
            </div>
            <div className="row">
              <div className="col-auto text-nowrap">Thumb Class:</div>
              <div className="col-auto text-break-line">
                {thumb.thumbClassPartNumber || '--'}
              </div>
            </div>
            <div className="row">
              <div className="col-auto text-nowrap">Factory Mount:</div>
              <div className="col-auto text-break-line">
                {thumb.factoryMount ? 'Yes' : 'No'}
              </div>
            </div>
          </div>
        )}
        {notes && (
          <div className="row mt-2">
            <div className="col">{notes}</div>
          </div>
        )}
        {restrictedReason && (
          <div className="row mt-2">
            <div className="col">
              <span className="badge text-bg-danger p-1">
                {restrictedReason}
              </span>
            </div>
          </div>
        )}
        {isConsignment && (
          <div className="row mt-2">
            <div className="col">
              <div className="alert alert-info text-start p-2 mb-0">
                Consignment {consignmentDealerBranchName && <span> at {consignmentDealerBranchName}</span>}
              </div>
            </div>
          </div>
        )}
      </td>
      <td data-title="Condition">

        {isNewCondition && (
          <div className="badge text-bg-warning">
            {displayCondition}
          </div>
        )}
        {!isNewCondition && displayCondition}
      </td>
      <td data-title="Compatibility">
        <div>{carrierName}</div>
        {weightCategoriesList(inventoryItem.weightCategoryLabels)}
        {lugging.pinUpName && (
          <div>
            {`Direct Pin On: ${lugging.pinUpName}`}
          </div>
        )}
        {lugging.couplerNames && lugging.couplerNames.length > 0 && (
          <div>
            <span>Lugging: </span>
            <span>
              {lugging.couplerNames.join(", ")}
            </span>
          </div>
        )}
      </td>
      <td data-title="Availability">
        {inventoryItem.sold ? ('Sold') : (<Availability inventoryItem={inventoryItem} />)}
        <div>Qty: {inventoryItem.quantity}</div>
      </td>

      <td >
        <div className="row d-flex flex-row justify-content-center">
          {!inventoryItem.isAddedToQuote && (
            <button
              type="button"
              className={`select-button btn ${isItemSelected ? 'btn-success' : 'btn-primary'} btn-sm`}
              onClick={() => onProductSelected(inventoryItem.productId, inventoryItem.id)}
              disabled={!canSelectInventoryItem || (highlightedInventoryItemId && inventoryItem.incompatible)}
            >
              {isItemSelected ? 'Selected' : 'Select'}
            </button>
          )}

          {inventoryItem.incompatible && (
            <div className="badge text-bg-danger py-1 my-2 me-3" style={{ left: 0, transform: "none" }}><strong>Incompatible</strong></div>
          )}

          {inventoryItem.isAddedToQuote && (
            <div className="alert alert-warning my-1 p-1 me-3" style={{ left: 0, transform: "none" }}>
              <strong>Item already added. To make changes, please go to the Review step</strong>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

Row.propTypes = {
  inventoryItem: PropTypes.instanceOf(QuoteInventoryItemViewModel).isRequired,
  product: PropTypes.object.isRequired,
  productGroup: PropTypes.object.isRequired,
  onProductSelected: PropTypes.func.isRequired,
  productId: PropTypes.string,
  inventoryItemId: PropTypes.string,
  canSelectInventoryItem: PropTypes.bool.isRequired,
  highlightedInventoryItemId: PropTypes.string,
};

export default Row;
